import React from 'react';
import { DocumentTextIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

const TermsConditions: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <DocumentTextIcon className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms & Conditions</h1>
          <p className="text-xl text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
          
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <ShieldCheckIcon className="h-6 w-6 text-green-600 mr-2" />
              1. Acceptance of Terms
            </h2>
            <p className="text-gray-700 leading-relaxed">
              By accessing and using Rightoz services, including our website and mobile application, 
              you accept and agree to be bound by the terms and provision of this agreement. 
              If you do not agree to abide by the above, please do not use this service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Service Description</h2>
            <div className="text-gray-700 leading-relaxed space-y-3">
              <p>Rightoz provides:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Fresh milk delivery services</li>
                <li>Dairy product subscription services</li>
                <li>Quality assurance and hygiene standards</li>
                <li>Flexible delivery scheduling</li>
                <li>Customer support services</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Responsibilities</h2>
            <div className="text-gray-700 leading-relaxed space-y-3">
              <p>As a user of Rightoz services, you agree to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide accurate and complete information during registration</li>
                <li>Maintain the security of your account credentials</li>
                <li>Be available during scheduled delivery times</li>
                <li>Handle delivered products with care</li>
                <li>Report any issues or concerns promptly</li>
                <li>Make timely payments for services rendered</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Delivery Policy</h2>
            <div className="text-gray-700 leading-relaxed space-y-3">
              <p><strong>Delivery Schedule:</strong> We offer morning and evening delivery slots based on your location and preference.</p>
              <p><strong>Delivery Area:</strong> Services are currently available in selected areas. Please check availability in your location.</p>
              <p><strong>Missed Deliveries:</strong> If no one is available to receive the delivery, we will attempt to contact you. Repeated missed deliveries may result in service suspension.</p>
              <p><strong>Quality Assurance:</strong> All products are quality-checked before delivery. Any quality issues should be reported immediately.</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Payment Terms</h2>
            <div className="text-gray-700 leading-relaxed space-y-3">
              <p>Payment can be made through:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Cash on delivery</li>
                <li>Digital payments (UPI, Credit/Debit cards)</li>
                <li>Wallet payments</li>
                <li>Monthly subscription plans</li>
              </ul>
              <p className="mt-3">All payments are processed securely. Late payments may result in service interruption.</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Subscription Services</h2>
            <div className="text-gray-700 leading-relaxed space-y-3">
              <p><strong>Subscription Plans:</strong> We offer daily, weekly, and monthly subscription plans.</p>
              <p><strong>Modifications:</strong> Subscription modifications can be made through our app or by contacting customer support.</p>
              <p><strong>Pause/Resume:</strong> Subscriptions can be paused for vacation or temporary needs with advance notice.</p>
              <p><strong>Cancellation:</strong> Subscriptions can be cancelled with 24-48 hours notice.</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              Your privacy is important to us. We collect and use your personal information to provide 
              and improve our services. We do not share your personal information with third parties 
              without your consent, except as required by law or as necessary to provide our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              Rightoz strives to provide high-quality products and services. However, we cannot guarantee 
              uninterrupted service and shall not be liable for any indirect, incidental, or consequential 
              damages arising from the use of our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Contact Information</h2>
            <div className="bg-green-50 rounded-lg p-6 border border-green-200">
              <p className="text-gray-700 mb-3">For any questions regarding these terms and conditions, please contact us:</p>
              <div className="space-y-2 text-sm">
                <p><strong>Email:</strong> support@rightoz.com</p>
                <p><strong>Phone:</strong> +91 91144 91158</p>
                <p><strong>Address:</strong> Plot No 48, Deep Vihar Dadi Ka Phatak, Benad Road Jhatwara, Jaipur, Rajasthan 302012</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              Rightoz reserves the right to modify these terms and conditions at any time. 
              Users will be notified of significant changes via email or app notifications. 
              Continued use of our services after changes constitutes acceptance of the new terms.
            </p>
          </section>

        </div>

        {/* Back to Home */}
        <div className="text-center mt-12">
          <a
            href="/"
            className="inline-flex items-center bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
